import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  InputAdornment,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import { Add, Search, Edit, Delete } from "@mui/icons-material";
import { useCategories } from "../hooks/useCategories";
import { apiService } from "../api/services/apiService";
import { PaginationControls } from "./common/PaginationControls";
import { useNavigate } from "react-router-dom";
import { Category, EntityStatus } from "../types";

const ITEMS_PER_PAGE = 10;

const ManageCategories: React.FC = () => {
  const navigate = useNavigate();
  const { categories: allCategories, loading, error } = useCategories();

  const [categories, setCategories] = useState<Category[]>([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [deleteUuid, setDeleteUuid] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [loadingCategory, setLoadingCategory] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [successOpen, setSuccessOpen] = useState(false);

  /* -------------------- Load Data -------------------- */
  useEffect(() => {
    setCategories(allCategories);
  }, [allCategories]);

  /* -------------------- Edit Category -------------------- */
  const handleEditClick = async (uuid: string) => {
    setLoadingCategory(true);
    setEditingCategory(null);

    try {
      const category = await apiService.getCategory(uuid);
      setEditingCategory(category);
      setEditModalOpen(true);
    } catch (err: any) {
      console.error(err);
      alert(err.message || "Failed to fetch category");
    } finally {
      setLoadingCategory(false);
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory || !editingCategory.uuid) {
      return;
    }

    setUpdating(true);
    try {
      await apiService.updateCategory(editingCategory.uuid, {
        name: editingCategory.name,
        status: editingCategory.status,
      });

      setCategories((prev) =>
        prev.map((c) =>
          c.uuid === editingCategory.uuid ? editingCategory : c,
        ),
      );

      setSuccessOpen(true);
      setEditModalOpen(false);
    } catch (err: any) {
      console.error(err);
      alert(err.message || "Failed to update category");
    } finally {
      setUpdating(false);
    }
  };

  /* -------------------- Delete Category -------------------- */
  const handleDeleteClick = (uuid: string) => {
    setDeleteUuid(uuid);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteUuid) return;

    setIsDeleting(true);
    try {
      const success = await apiService.deleteCategory(deleteUuid);
      if (success) {
        setCategories((prev) => prev.filter((c) => c.uuid !== deleteUuid));
      }
    } catch (err: any) {
      console.error(err);
      alert(err.message || "Failed to delete category");
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setDeleteUuid(null);
    }
  };

  /* -------------------- Filtering & Pagination -------------------- */
  const filteredCategories = categories.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()),
  );

  const totalPages = Math.ceil(filteredCategories.length / ITEMS_PER_PAGE);
  const paginatedCategories = filteredCategories.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          Categories
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search by name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/manage-assets/categories/add")}
          >
            Add Category
          </Button>
        </Box>
      </Box>

      {/* Loading / Error */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Typography color="error">{error}</Typography>}

      {/* Table */}
      {!loading && !error && (
        <>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedCategories.length ? (
                  paginatedCategories.map((category) => (
                    <TableRow key={category.uuid} hover>
                      <TableCell>{category.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={category.status}
                          size="small"
                          color={
                            category.status === "ACTIVE" ? "success" : "default"
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(category.uuid)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(category.uuid)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No categories found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {/* Delete Dialog */}
      <Dialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <strong>Delete Category?</strong>
        </DialogTitle>
        <DialogContent>
          Are you sure you want to delete this category?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteModalOpen(false)}
            variant="outlined"
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            No
          </Button>
          <Button
            color="error"
            variant="contained"
            sx={{ textTransform: "none", fontWeight: 600 }}
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        fullWidth
      >
        <DialogTitle>Edit Category</DialogTitle>
        <DialogContent>
          {loadingCategory || !editingCategory ? (
            <CircularProgress />
          ) : (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
            >
              <TextField
                label="Name"
                value={editingCategory.name}
                onChange={(e) =>
                  setEditingCategory({
                    ...editingCategory,
                    name: e.target.value,
                  })
                }
              />

              <TextField
                label="Tenant ID"
                value={editingCategory.tenantId}
                disabled
              />

              <TextField
                select
                label="Status"
                value={editingCategory.status}
                onChange={(e) =>
                  setEditingCategory({
                    ...editingCategory,
                    status: e.target.value as EntityStatus,
                  })
                }
              >
                {Object.values(EntityStatus).map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateCategory}
            disabled={updating || !editingCategory}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={successOpen}
        autoHideDuration={2000}
        onClose={() => setSuccessOpen(false)}
      >
        <Alert severity="success">Category updated successfully</Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageCategories;
